import { useEffect, useMemo, useRef, useState } from 'react'
import * as PreenchimentoHttp from '../../../../services/api/https/checklist-operacional/preenchimento.http'
import { errorHandler } from '../../../../services/util/error.service'
import { PreenchimentoStatus } from '../../../../constants/checklist-operacional'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  INITIAL_GET: 'Carregando preenchimentos',
  SAVE: 'Salvando preenchimento',
  DELETE: 'Excluindo preenchimento',
}

export const ACOES = {
  EDITAR: '1. Editar',
  IMPRIMIR: '2. Imprimir',
  PREENCHER: '3. Preencher',
  VER_RESULTADO: '4. Ver Resultado',
  IMPRIMIR_RESULTADO: '5. Imprimir Resultado',
  EXCLUIR: '6. Excluir',
}

export const MENU_ITEMS = Object.keys(ACOES).map(chave => ACOES[chave])

const FORM_INIT_VAZIO = { checklists: [], users: [] }

const INITIAL_STATE = {
  preenchimentos: [],
  formInit: FORM_INIT_VAZIO,
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
  successMessage: null,
  selectedTp: 0,
  globalFilter: '',
  showModal: false,
  preenchimentoEdicao: null,
  preenchimentoSalvar: null,
  preenchimentoExcluir: null,
}

export function validarAcao(acao, preenchimento) {
  const finalizado = preenchimento.status === PreenchimentoStatus.Finalizado.label
  const expirado = preenchimento.status === PreenchimentoStatus.Expirado.label

  switch (acao) {
    case ACOES.EDITAR:
      return finalizado ? 'Preenchimento finalizado não pode ser alterado' : null
    case ACOES.PREENCHER:
      if (finalizado) return 'Preenchimento finalizado não pode ser alterado'
      if (expirado)
        return 'Preenchimento expirado não pode ser preenchido. Altere o deadline para reativá-lo'
      return null
    case ACOES.VER_RESULTADO:
    case ACOES.IMPRIMIR_RESULTADO:
      return finalizado ? null : 'Somente preenchimentos finalizados possuem resultado'
    case ACOES.EXCLUIR:
      return finalizado ? 'Preenchimento finalizado não pode ser excluído' : null
    default:
      return null
  }
}

export default function usePreenchimentoChecklist() {
  const [state, setState] = useState({ ...INITIAL_STATE })
  const montado = useRef(true)

  useEffect(() => {
    return () => {
      montado.current = false
    }
  }, [])

  const localDispatch = useMemo(() => {
    const falhar = (err, extras = {}) => {
      if (!montado.current) return

      setState(prev => ({
        ...prev,
        loadingMessage: LOAD_MESSAGE.NONE,
        errorMessage: errorHandler(err),
        ...extras,
      }))
    }

    return {
      async getPreenchimentos(tpId) {
        try {
          const [preenchimentos, formInit] = await Promise.all([
            PreenchimentoHttp.getPreenchimentos(tpId),
            PreenchimentoHttp.getPreenchimentoFormInit(tpId),
          ])
          if (!montado.current) return

          setState(prev => ({
            ...prev,
            preenchimentos,
            formInit,
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        } catch (err) {
          falhar(err)
        }
      },
      async save(tpId, preenchimento) {
        try {
          await PreenchimentoHttp.savePreenchimento(tpId, preenchimento)
          if (!montado.current) return

          setState(prev => ({
            ...prev,
            showModal: false,
            preenchimentoEdicao: null,
            preenchimentoSalvar: null,
            loadingMessage: LOAD_MESSAGE.INITIAL_GET,
            successMessage: 'Preenchimento salvo com sucesso',
          }))
        } catch (err) {
          falhar(err)
        }
      },
      async delete(tpId, preenchimento) {
        try {
          await PreenchimentoHttp.deletePreenchimento(tpId, preenchimento.id)
          if (!montado.current) return

          setState(prev => ({
            ...prev,
            preenchimentoExcluir: null,
            loadingMessage: LOAD_MESSAGE.INITIAL_GET,
            successMessage: 'Preenchimento excluído com sucesso',
          }))
        } catch (err) {
          falhar(err, { preenchimentoExcluir: null })
        }
      },
      onInit(selectedTp) {
        setState({
          ...INITIAL_STATE,
          selectedTp,
          loadingMessage: selectedTp ? LOAD_MESSAGE.INITIAL_GET : LOAD_MESSAGE.NONE,
          errorMessage: selectedTp ? null : 'Selecione um Operador Logístico',
        })
      },
      onGlobalFilterChange(value) {
        setState(prev => ({ ...prev, globalFilter: value }))
      },
      toggleModal(show, preenchimento = null) {
        setState(prev => ({ ...prev, showModal: show, preenchimentoEdicao: preenchimento }))
      },
      onModalSave(preenchimento) {
        setState(prev => ({
          ...prev,
          preenchimentoSalvar: preenchimento,
          loadingMessage: LOAD_MESSAGE.SAVE,
        }))
      },
      onExcluirClicked(preenchimento) {
        setState(prev => ({ ...prev, preenchimentoExcluir: preenchimento }))
      },
      onConfirmExcluir(confirmado) {
        setState(prev => ({
          ...prev,
          preenchimentoExcluir: confirmado ? prev.preenchimentoExcluir : null,
          loadingMessage: confirmado ? LOAD_MESSAGE.DELETE : prev.loadingMessage,
        }))
      },
      setError(errorMessage) {
        setState(prev => ({ ...prev, errorMessage }))
      },
      clearMessages() {
        setState(prev => ({ ...prev, errorMessage: null, successMessage: null }))
      },
    }
  }, [])

  return { state, localDispatch }
}
